import { type InputHTMLAttributes, forwardRef } from 'react'
import { cn } from '../lib/cn'

export type SliderProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'min' | 'max'> & {
  value: number
  min: number
  max: number
}

/**
 * Styled range input: the track fills with the primary color up to the current
 * value (consistent across browsers, unlike `accent-color`), white round thumb.
 */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  ({ className, value, min, max, style, ...props }, ref) => {
    const pct = max > min ? ((value - min) / (max - min)) * 100 : 0
    return (
      <input
        ref={ref}
        type="range"
        value={value}
        min={min}
        max={max}
        style={{
          background: `linear-gradient(to right, var(--primary) ${pct}%, var(--surface-elevated) ${pct}%)`,
          ...style,
        }}
        className={cn(
          'h-1.5 w-full cursor-pointer appearance-none rounded-full border border-border/60 outline-none',
          // WebKit thumb
          '[&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none',
          '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary',
          '[&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-sm',
          '[&::-webkit-slider-thumb]:transition-transform hover:[&::-webkit-slider-thumb]:scale-110 active:[&::-webkit-slider-thumb]:scale-95',
          // Firefox thumb + track
          '[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full',
          '[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-sm',
          '[&::-moz-range-track]:bg-transparent',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
    )
  },
)
Slider.displayName = 'Slider'
